import { useMemo } from 'react';
import { Document, Command, Person } from '../types';

interface UseEnhancedDropdownProps {
  documents: Document[];
  commands: Command[];
  people: Person[];
  searchTerm: string;
  activeCategory: string;
  dropdownType: 'document' | 'person' | 'command';
  linkedDocumentIds?: string[];
}

export const useEnhancedDropdown = ({
  documents,
  commands,
  people,
  searchTerm,
  activeCategory,
  dropdownType,
  linkedDocumentIds = [],
}: UseEnhancedDropdownProps) => {
  const documentCategories = useMemo(() => {
    if (dropdownType === 'person') {
      // No categories for people
      return {};
    } else if (dropdownType === 'document') {
      // Only show document-related categories
      return {
        all: 'All',
        recent: 'Recent',
        documents: 'Documents',
      };
    } else {
      // Commands will be added later
      return {};
    }
  }, [dropdownType]);

  const filteredSuggestions = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    // Clean search term (remove @ or # if present)
    const cleanSearchTerm = lowerSearchTerm.replace(/^[@#]/, '');

    let filtered: (Document | Command | Person)[] = [];

    // Filter based on dropdownType
    if (dropdownType === 'person') {
      // Only show people when @ is typed
      filtered = people.filter((person) =>
        person.name.toLowerCase().includes(cleanSearchTerm)
      );
    } else if (dropdownType === 'document') {
      // Only show documents when # is typed. Show ONLY linked documents.
      // If none are linked, show none (user must link via Active Documents or /documents first).
      const linkedDocs =
        linkedDocumentIds.length > 0
          ? documents.filter((doc) => {
              const docId = doc.dataRoomFileId || String(doc.id);
              return linkedDocumentIds.includes(docId);
            })
          : [];

      filtered = linkedDocs.filter((doc) =>
        doc.name.toLowerCase().includes(cleanSearchTerm)
      );

      // Apply category filter for documents
      if (activeCategory !== 'all' && activeCategory !== 'recent') {
        filtered = filtered.filter(
          (item) => (item as Document).category === activeCategory
        );
      }
    } else if (dropdownType === 'command') {
      // Commands will be added later, for now return empty
      filtered = [];
    }

    return filtered;
  }, [documents, commands, people, searchTerm, activeCategory, dropdownType, linkedDocumentIds]);

  return {
    documentCategories,
    filteredSuggestions,
  };
};
